import React, { useEffect, useState } from 'react'
import { FiSearch } from 'react-icons/fi'
import { useTranslation } from '../i18n/LanguageContext'
import { getCarBrands, getCarModels, getCarFuelTypes } from '../lib/backend'
import Flag from './CountryFlag'
import { SCRAPED_COUNTRIES, countryName, toFlagCode } from '../lib/countries.js'
import './SearchFilters.css'

/** SIMPLE-1: the buyer's own selection only. The served safeguards (fresh, priced, not sold,
 * no price anomaly, released) are not choices: they are always on, server-side. */
export const emptyFilters = () => ({
  make: '', model: [], fuel: [], countries: [], minYear: '', maxYear: '', maxMileage: '', maxPrice: '', mode: 'picks',
})
const SOURCE_COUNTRIES = SCRAPED_COUNTRIES.filter(code => code !== 'NL')

function SearchFilters({ filters, setFilters, onSearch }) {
  const { t } = useTranslation()
  const [brands, setBrands] = useState([])
  const [models, setModels] = useState([])
  const [fuels, setFuels] = useState([])
  // Open beside the list on a wide screen; folded on a phone so the cars come first.
  const [startOpen] = useState(() => typeof window === 'undefined' || !window.matchMedia
    || window.matchMedia('(min-width: 1025px)').matches)
  const active = ['make', 'minYear', 'maxYear', 'maxMileage', 'maxPrice'].filter(name => filters[name]).length
    + ['model', 'fuel', 'countries'].filter(name => filters[name].length).length

  useEffect(() => {
    getCarBrands().then(list => setBrands(list || [])).catch(() => setBrands([]))
    getCarFuelTypes().then(list => setFuels(list || [])).catch(() => setFuels([]))
  }, [])
  useEffect(() => {
    let live = true
    setModels([])
    if (filters.make) getCarModels(filters.make).then(list => live && setModels(list || [])).catch(() => live && setModels([]))
    return () => { live = false }
  }, [filters.make])

  const set = (name, value) => setFilters(prev => ({ ...prev, [name]: value,
    ...(name === 'make' && value !== prev.make ? { model: [] } : {}) }))
  const toggleCountry = code => setFilters(prev => ({ ...prev, countries: prev.countries.includes(code)
    ? prev.countries.filter(c => c !== code) : [...prev.countries, code] }))
  const number = name => (
    <input type="number" inputMode="numeric" min="0" value={filters[name]}
      onChange={e => set(name, e.target.value)} />
  )

  return (
    <details className="filters-fold" open={startOpen}>
    <summary>{t('Filters')}{active ? ` (${t('{count} active', { count: active })})` : ''}</summary>
    <form className="search-filters" onSubmit={e => { e.preventDefault(); onSearch(filters) }}>
      <div className="filter-grid">
        <label>{t('Make')}
          <select value={filters.make} onChange={e => set('make', e.target.value)}>
            <option value="">{t('All makes')}</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <label>{t('Model')}
          <select value={filters.model[0] || ''} disabled={!filters.make}
            onChange={e => set('model', e.target.value ? [e.target.value] : [])}>
            <option value="">{filters.make ? t('All models') : t('Choose a make first')}</option>
            {models.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label>{t('Fuel')}
          <select value={filters.fuel[0] || ''} onChange={e => set('fuel', e.target.value ? [e.target.value] : [])}>
            <option value="">{t('All fuels')}</option>
            {fuels.map(f => <option key={f} value={f}>{t(f)}</option>)}
          </select>
        </label>
        <label>{t('Year from')}{number('minYear')}</label>
        <label>{t('Year to')}{number('maxYear')}</label>
        <label>{t('Max. mileage (km)')}{number('maxMileage')}</label>
        <label>{t('Max. price (€)')}{number('maxPrice')}</label>
      </div>
      <fieldset className="country-chips">
        <legend>{t('Countries')} <small>{filters.countries.length ? '' : t('(all)')}</small></legend>
        {SOURCE_COUNTRIES.map(code => (
          <button type="button" key={code} aria-pressed={filters.countries.includes(code)}
            className={filters.countries.includes(code) ? 'on' : ''} onClick={() => toggleCountry(code)}>
            <Flag code={toFlagCode(code)} /> {t(countryName(code))}
          </button>
        ))}
      </fieldset>
      <div className="filter-actions">
        <button type="submit" className="btn-search"><FiSearch /> {t('Search')}</button>
        <button type="button" className="btn-reset" onClick={() => setFilters(emptyFilters())}>{t('Reset')}</button>
      </div>
    </form>
    </details>
  )
}

export default SearchFilters
